import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import type { Property, PropertySummary, PropertyStatus, UpdatePropertyRequest, CreatePropertyRequest, ListPropertiesTierInfo } from '../types/api';

interface UsePropertiesReturn {
  properties: PropertySummary[];
  loading: boolean;
  error: string | null;
  selectedProperty: Property | null;
  selectedPropertyLoading: boolean;
  statusFilter: PropertyStatus | 'all' | 'excluded_rejected';
  setStatusFilter: (status: PropertyStatus | 'all' | 'excluded_rejected') => void;
  selectProperty: (id: string | null) => void;
  createProperty: (data: Omit<CreatePropertyRequest, 'searchId'>) => Promise<string>;
  updateProperty: (id: string, data: UpdatePropertyRequest) => Promise<void>;
  deleteProperty: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  tierInfo: ListPropertiesTierInfo | null;
  compareIds: Set<string>;
  toggleCompare: (id: string) => void;
  clearCompare: () => void;
  loadedForSearchId: string | null;
}

export function useProperties(searchId: string | null): UsePropertiesReturn {
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [selectedPropertyLoading, setSelectedPropertyLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PropertyStatus | 'all' | 'excluded_rejected'>('all');
  const [tierInfo, setTierInfo] = useState<ListPropertiesTierInfo | null>(null);
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set());
  const [loadedForSearchId, setLoadedForSearchId] = useState<string | null>(null);

  const loadProperties = useCallback(async () => {
    if (!searchId) {
      setProperties([]);
      setTierInfo(null);
      setLoading(false);
      setLoadedForSearchId(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.listProperties(searchId);
      setProperties(response.properties);
      setTierInfo(response.tierInfo ?? null);
      setLoadedForSearchId(searchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load properties');
      setLoadedForSearchId(searchId);
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  // Clear selection when search changes
  const prevSearchId = useRef(searchId);
  useEffect(() => {
    if (prevSearchId.current !== searchId) {
      setSelectedId(null);
      setSelectedProperty(null);
      setSelectedPropertyLoading(false);
      prevSearchId.current = searchId;
    }
  }, [searchId]);

  // Fetch full property when selectedId changes
  useEffect(() => {
    if (!selectedId || !searchId) {
      setSelectedProperty(null);
      setSelectedPropertyLoading(false);
      return;
    }

    let cancelled = false;
    setSelectedPropertyLoading(true);

    api.getProperty(searchId, selectedId).then((full) => {
      if (!cancelled) {
        setSelectedProperty(full);
        setSelectedPropertyLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setSelectedProperty(null);
        setSelectedPropertyLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [selectedId, searchId]);

  const selectProperty = useCallback((id: string | null) => {
    setSelectedId(id);
    if (!id) {
      setSelectedProperty(null);
    }
  }, []);

  const createProperty = useCallback(async (data: Omit<CreatePropertyRequest, 'searchId'>): Promise<string> => {
    if (!searchId) throw new Error('No search selected');
    const response = await api.createProperty({ ...data, searchId });
    await loadProperties();
    return response.id;
  }, [searchId, loadProperties]);

  const updateProperty = useCallback(async (id: string, data: UpdatePropertyRequest) => {
    if (!searchId) return;
    const response = await api.updateProperty(searchId, id, data);

    // Update the summary in the list with fields that overlap
    setProperties((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        return {
          ...p,
          address: response.address,
          rent: response.rent,
          status: response.status,
          bedrooms: response.bedrooms,
          bathrooms: response.bathrooms,
          userRating: response.userRating,
          viewingDate: response.viewingDate ?? undefined,
          updatedAt: response.updatedAt,
        };
      })
    );

    // Update the full selected property if it's the one being edited
    setSelectedProperty((prev) => {
      if (!prev || prev.id !== id) return prev;
      return {
        ...prev,
        ...response,
        viewingDate: response.viewingDate ?? undefined,
        contactName: response.contactName ?? undefined,
        contactPhone: response.contactPhone ?? undefined,
        councilTaxYearly: response.councilTaxYearly ?? undefined,
        utilitiesMonthly: response.utilitiesMonthly ?? undefined,
      };
    });
  }, [searchId]);

  const deleteProperty = useCallback(async (id: string) => {
    if (!searchId) return;
    await api.deleteProperty(searchId, id);
    setProperties((prev) => prev.filter((p) => p.id !== id));
    setSelectedId(null);
    setSelectedProperty(null);
    setCompareIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }, [searchId]);

  const toggleCompare = useCallback((id: string) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 4) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearCompare = useCallback(() => {
    setCompareIds(new Set());
  }, []);

  return {
    properties,
    loading,
    error,
    selectedProperty,
    selectedPropertyLoading,
    statusFilter,
    setStatusFilter,
    selectProperty,
    createProperty,
    updateProperty,
    deleteProperty,
    refresh: loadProperties,
    tierInfo,
    compareIds,
    toggleCompare,
    clearCompare,
    loadedForSearchId,
  };
}
